/* storybook-check-ignore */
import { useState } from 'react';

import { useAuth } from '@opendoor/auth-fe';
import { Box, Button, Dialog, Text, TextButton, TextInput } from '@opendoor/bricks-next';
import { globalObservability } from '@opendoor/observability/slim';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';

import { OdProtosSellReceptionData_SellerInput_Channel } from '__generated__/athena';

import { useT } from 'helpers/i18n';

import {
  ADDRESS_INVALID_RESULT,
  gateProceedToOnboardingFlow,
  inOffMarketPdpExperiment,
  SelectedAddress,
  useAddressValidationContext,
} from '../../helpers/AddressValidationContext';
import { useObservability } from '../../helpers/observability';
import { standardizeAddress } from '../api';
import { AddressSearchLocation, TrackingTaxonomy } from '../globals';
import { getAnonymousId } from '../shared/trackers';

const Sentry = globalObservability.getSentryClient();

export const extractStreetNumber = (address: SelectedAddress | null) => {
  const regex = /\b\d+[A-Za-z]*(?:-\d+[A-Za-z]*)?\b/;
  const matches = address?.street1?.match(regex);
  return matches ? matches[0] : null;
};

export const addressNotFound = (errorData: string | { message: string }) => {
  const msg = typeof errorData == 'string' ? errorData : errorData.message;
  return msg.indexOf('NOT_FOUND') === 2;
};

export const AddressConfirmationModal = ({
  isOpen,
  setIsOpen,
  partnerReferralId,
  initialAddress,
  pageSetPath,
  trackingKeywords,
  channel,
  inputLocation,
  trackingTaxonomy,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  partnerReferralId?: string;
  initialAddress: SelectedAddress;
  pageSetPath?: string;
  trackingKeywords?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
  inputLocation: AddressSearchLocation;
  trackingTaxonomy: TrackingTaxonomy;
}) => {
  const { t } = useT();
  const router = useRouter();
  const { trackEvent } = useObservability();
  const { addressResults, setValidatedAddress, validatedAddress } = useAddressValidationContext();
  const [address, setAddress] = useState<SelectedAddress>({ ...initialAddress });
  const [unitNumberErrorText, setUnitNumberErrorText] = useState<string>('');
  const [isCreateSellerInputLoading, setIsCreateSellerInputLoading] = useState<boolean>(false);
  const [isNoUnitLoading, setIsNoUnitLoading] = useState<boolean>(false);
  const [confirmSameUnitStreet, setConfirmSameUnitStreet] = useState<boolean>(false);
  const { user } = useAuth();
  const localeCookie = Cookies.get('locale');
  const anonId = getAnonymousId();
  const isAddressUnitNumberMissing = addressResults?.includes('ADDRESS_MISSING_SUB_PREMISE');
  const streetNumber = extractStreetNumber(initialAddress);

  const validateUnitInput = async (): Promise<SelectedAddress | undefined> => {
    try {
      const response = await standardizeAddress(address);
      if (
        !response ||
        !response.data ||
        (response.errors && !addressNotFound(response.errors[0]))
      ) {
        setUnitNumberErrorText(
          t(
            'addressConfirmationModal.errors.unexpected',
            'An unexpected error occurred. Please try again.',
          ),
        );
        return;
      }
      const casaAddress = response.data?.casa.findByFields?.address;
      if (
        casaAddress?.result.includes(ADDRESS_INVALID_RESULT) ||
        (response.errors && addressNotFound(response.errors[0]))
      ) {
        setUnitNumberErrorText(
          t(
            'addressConfirmationModal.errors.unitInvalid',
            'Unit number not found or entered incorrectly',
          ),
        );
        return;
      }
      return {
        street1: casaAddress.street,
        city: casaAddress.city,
        state: casaAddress.state,
        postal_code: casaAddress.postalCode,
        unit: casaAddress.unitNumber,
        id: casaAddress.uuid,
      };
    } catch (error) {
      Sentry.captureException?.(error);
      setUnitNumberErrorText(
        t(
          'addressConfirmationModal.errors.unexpected',
          'An unexpected error occurred. Please try again.',
        ),
      );
      return;
    }
  };

  const routeToManualAddressPage = () => {
    router.push({
      pathname: '/manual-address-entry',
      query: {
        address: JSON.stringify(validatedAddress),
        ...(trackingKeywords != null && { trackingKeywords }),
        ...(pageSetPath != null && { pageSetPath }),
        ...(partnerReferralId != null && { partnerReferralId }),
        ...(channel != null && { channel }),
      },
    });
  };

  const proceedWithAddress = async (addressToUse: SelectedAddress) => {
    setValidatedAddress(addressToUse);

    // Only check experiment if unit number was missing
    let inExperiment = false;
    if (isAddressUnitNumberMissing) {
      inExperiment = await inOffMarketPdpExperiment(user?.customerUuid || '', localeCookie, anonId);
    }

    if (inExperiment) {
      router.push({
        pathname: `/property-detail/${addressToUse.id}`,
        query: {
          ...(trackingKeywords != null && { trackingKeywords }),
          ...(pageSetPath != null && { pageSetPath }),
          ...(partnerReferralId != null && { partnerReferralId }),
          ...(channel != null && { channel }),
        },
      });
    } else {
      await gateProceedToOnboardingFlow({
        address: addressToUse,
        product: 'SELL_DIRECT',
        trackEvent,
        partnerReferralId,
        addressUUID: addressToUse.id,
        pageSetPath,
        trackingKeywords,
        channel,
        inputLocation,
        trackingTaxonomy,
      })
        .catch((error) => {
          Sentry.captureException?.(error);
          setUnitNumberErrorText(
            t(
              'addressConfirmationModal.errors.unexpected',
              'An unexpected error occurred. Please try again.',
            ),
          );
        })
        .finally(() => {
          setIsCreateSellerInputLoading(false);
          setIsNoUnitLoading(false);
        });
    }
  };

  const handleNoUnit = async () => {
    await proceedWithAddress(address);
  };

  const handleConfirmAddress = async () => {
    if (isAddressUnitNumberMissing) {
      // Unit number is required but missing
      if (!address?.unit) {
        setUnitNumberErrorText(
          t(
            'addressConfirmationModal.errors.unitMissing',
            'Enter a unit number or tap "No unit" below',
          ),
        );
        setIsCreateSellerInputLoading(false);
        return;
      }

      // Validate the unit input
      const validatedAddress = await validateUnitInput();
      if (validatedAddress) {
        await proceedWithAddress(validatedAddress);
      } else {
        setIsCreateSellerInputLoading(false);
      }
    } else {
      // No unit number needed, proceed directly
      await proceedWithAddress(address);
    }
  };

  const handleUnitNumberChange = (input: string) => {
    setConfirmSameUnitStreet(false);
    if (address) {
      setAddress({ ...address, unit: input });
    }
    const validUnitNumberRegex = /^[A-Za-z0-9\s-#]+$/;
    if (input && !validUnitNumberRegex.test(input)) {
      setUnitNumberErrorText(
        t(
          'addressConfirmationModal.errors.invalidCharacters',
          'Unit numbers can’t include special characters.',
        ),
      );
    } else if (input === streetNumber) {
      setUnitNumberErrorText(
        t(
          'addressConfirmationModal.errors.unitEqualsStreet',
          'If street and unit number are the same, tap "Same street and unit" below.',
        ),
      );
    } else {
      setUnitNumberErrorText('');
    }
  };

  return (
    <Dialog isOpen={isOpen} dismissable overlayColor="dark" setIsOpen={setIsOpen}>
      <Box width={600} $smallerThanLG={{ width: '100%' }}>
        <Dialog.Content
          title={
            isAddressUnitNumberMissing
              ? t('addressConfirmationModal.title.unitMissing', 'Is there a unit?')
              : t('addressConfirmationModal.title.default', 'Is this the correct home address?')
          }
          description=""
        >
          <Box flexDirection="column" gap={24} $smallerThanLG={{ width: '100%', gap: 16 }}>
            <Box
              flexDirection="row"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              p={24}
              backgroundColor="$backgroundStateInactiveTertiary"
              borderRadius="$16x"
              $smallerThanLG={{ padding: 16, borderRadius: '$8x' }}
            >
              <Text
                tag="p"
                typography="$bodyXlarge"
                width="60%"
                $smallerThanLG={{ typography: '$bodyLarge', width: '70%' }}
              >
                {address?.street1}
                {address?.unit ? `, #${address.unit}` : ''}
                <br />
                {address?.city}, {address?.state} {address?.postal_code}
              </Text>
              <TextButton
                analyticsName="cosmos-edit-address"
                label={t('addressConfirmationModal.buttons.edit', 'Edit')}
                onPress={routeToManualAddressPage}
                size="medium"
                variant="primary"
              />
            </Box>

            {isAddressUnitNumberMissing && (
              <>
                <TextInput
                  id="cosmos-address-confirmation-input"
                  size="medium"
                  title={t(
                    'addressConfirmationModal.labels.unitNumber',
                    'Add apt, suite, unit, etc.',
                  )}
                  required
                  placeholder={t('addressConfirmationModal.placeholders.unitNumber', 'Unit #1234')}
                  errorText={unitNumberErrorText}
                  onChangeText={handleUnitNumberChange}
                />
                <Box display="flex" flexDirection="column" gap={20}>
                  {address?.unit === streetNumber && !confirmSameUnitStreet && (
                    <TextButton
                      variant="primary"
                      id="same-street-unit"
                      label={t(
                        'addressConfirmationModal.buttons.sameStreetUnit',
                        'Same street and unit',
                      )}
                      size="large"
                      $smallerThanLG={{ size: 'medium', pt: 7 }}
                      analyticsName="cosmos-same-street-unit"
                      trackImpression
                      onPress={() => {
                        setUnitNumberErrorText('');
                        setConfirmSameUnitStreet(true);
                      }}
                    />
                  )}
                </Box>
              </>
            )}
            <Box
              flexDirection="row"
              justifyContent="space-between"
              gap={6}
              width="100%"
              $smallerThanLG={{ flexDirection: 'column', gap: 10 }}
            >
              {isAddressUnitNumberMissing && (
                <Button
                  width="50%"
                  analyticsName="address-confirmation-modal-no-unit"
                  label={t('addressConfirmationModal.buttons.noUnit', 'No unit')}
                  onPress={() => {
                    setIsNoUnitLoading(true);
                    handleNoUnit();
                  }}
                  size="medium"
                  variant="secondary"
                  $smallerThanLG={{ width: '100%' }}
                  loading={isNoUnitLoading}
                />
              )}
              <Button
                width={isAddressUnitNumberMissing ? '50%' : '100%'}
                analyticsName="address-confirmation-modal-confirm-address"
                label={t('addressConfirmationModal.buttons.confirmAddress', 'Confirm Address')}
                onPress={() => {
                  setIsCreateSellerInputLoading(true);
                  handleConfirmAddress();
                }}
                size="medium"
                variant="primary"
                $smallerThanLG={{ width: '100%' }}
                loading={isCreateSellerInputLoading}
                disabled={!!unitNumberErrorText}
              />
            </Box>
          </Box>
        </Dialog.Content>
      </Box>
    </Dialog>
  );
};
